import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { toastError } from '../../components/FormComponents/Toasts'
import Authentication from '../../models/Authentication'
import AuthenticationHistory from '../../models/AuthenticationHistory'
import Post from '../../models/Post'
import axiosService from '../../utils/AxiosService'

export const SharedInfluencerContext = React.createContext({})

export default observer(function SharedInfluencerProvider(props) {
  const { uuid } = useParams()

  const influencerState = useLocalObservable(() =>
    ({
      isLoading: true,
      authentication: new Authentication(),
      postsSortOrder: 'desc',
    })
  )

  const createSession = () => {
    if (!uuid) {
      influencerState.isLoading = false
      return Promise.reject('No UUID provided')
    }

    return axiosService.post(`/api/advertiser/influencer_share/sessions`, { shared_uuid: uuid })
      .then(() => {
        // Session created successfully
      })
      .catch(({ data: { error } }) => {
        toastError(error || 'Failed to create session')
        throw error
      })
  }

  const getAuthentication = () => {
    if (!uuid) {
      influencerState.isLoading = false
      return
    }

    // First create session, then load authentication
    createSession()
      .then(() => {
        return axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}`)
      })
      .then(authentication => {
        influencerState.authentication = new Authentication(authentication)
        influencerState.isLoading = false
        // Load additional data
        getAudienceInformation(influencerState.authentication)
        getAuthenticationHistories(influencerState.authentication)
        getPosts(influencerState.authentication)
        getSimilarAuthentications(influencerState.authentication)
        if (influencerState.authentication.provider === 'instagram') {
          getHashtags(influencerState.authentication)
          getMentions(influencerState.authentication)
        }
      })
      .catch(({ data: { error } }) => {
        toastError(error || 'Failed to load influencer')
        influencerState.isLoading = false
      })
  }

  const getAudienceInformation = (authentication) => {
    if (!authentication?.id || !uuid) return
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/audience_information`)
      .then(audience_information => authentication.audience_information = audience_information || null)
      .catch(({ data: { error } }) => console.error(error))
  }

  const getAuthenticationHistories = authentication => {
    if (!authentication?.id || !uuid) return
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/authentication_histories`)
      .then(authentication_histories => {
        authentication.authentication_histories = (authentication_histories || [])
          .filter(ah => ah)
          .map(authentication_history => new AuthenticationHistory(authentication_history))
      })
      .catch(({ data: { error } }) => console.error(error))
  }

  const getHashtags = authentication => {
    if (!authentication?.id || !uuid) return
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/authentications_hashtags`)
      .then(authentications_hashtags => authentication.authentications_hashtags = authentications_hashtags || [])
      .catch(({ data: { error } }) => console.error(error))
  }

  const getMentions = authentication => {
    if (!authentication?.id || !uuid) return
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/authentications_mentions`)
      .then(authentications_mentions => authentication.authentications_mentions = authentications_mentions || [])
      .catch(({ data: { error } }) => console.error(error))
  }

  const getPosts = (authentication, sortBy = 'desc') => {
    if (!authentication?.id || !uuid) return
    const params = sortBy ? { sort_by: sortBy } : {}
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/posts`, { params })
      .then(posts => authentication.posts = (posts || []).map(post => new Post(post)))
      .catch(({ data: { error } }) => console.error(error))
  }

  const getSimilarAuthentications = authentication => {
    if (!authentication?.id || !uuid) return
    axiosService.get(`/api/advertiser/influencer_share/authentications/${uuid}/similar_authentications`)
      .then(similar_authentications => authentication.similar_authentications = similar_authentications || [])
      .catch(({ data: { error } }) => console.error(error))
  }

  const setPostsSortOrder = (sortOrder) => {
    influencerState.postsSortOrder = sortOrder
    if (influencerState.authentication?.id) {
      getPosts(influencerState.authentication, sortOrder)
    }
  }

  useEffect(() => {
    getAuthentication()
  }, [uuid])

  return (
    <SharedInfluencerContext.Provider
      value={{
        influencerState,
        authentication: influencerState.authentication,
        authentications: [influencerState.authentication], // Array for compatibility
        isLoading: influencerState.isLoading,
        postsSortOrder: influencerState.postsSortOrder,
        setPostsSortOrder,
        getPosts: (auth, sortBy) => getPosts(auth, sortBy),
      }}
    >
      {props.children}
    </SharedInfluencerContext.Provider>
  )
})
